import {
  Checkbox,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Toast,
} from "flowbite-react";
import { useQuery } from "react-query";
import { QUERIES } from "../../../config/apiConfig";
import axios from "../../../config/axiosInstance";
import { useMemo, useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { HiExclamationCircle } from "react-icons/hi";

/** Grupos principais (cada um pode ter dependentes opcionais) */
const groupDefinitions = {
  "additional-avalia-max-plus-pf": {
    label: "Avalia Max Plus PF",
    dependents: [
      // Exemplo de dependente opcional (adicione se existir na sua base):
      // { url: "additional-avalia-cadastral-pf-plus", label: "Cadastral Plus" },
    ],
  },
  "additional-avalia-risco-completo-positivo-pf": {
    label: "Avalia Risco Completo Positivo PF",
    dependents: [
      // { url: "additional-avalia-score-positivo-pf", label: "Score Positivo" },
    ],
  },
};

// URLs usadas pela regra automática de risco completo
const RISCO_COMPLETO_PF = "additional-avalia-risco-completo-positivo-pf";
const PROTESTO_AVANCADO_PF = "additional-avalia-protesto-nacional-avancado-pf";

const AdditionalQueryAdvancedPF = ({
  selectedQueries,
  setSelectedQueries,
  type = "F",
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [warning, setWarning] = useState("");

  // Busca do que a empresa possui (filtra por PF com `type`)
  const { data: queryAdditionals = [] } = useQuery(
    ["queryAdditionals", type],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_ADDITIONALS_BY_COMPANY}?type=${type}`
      );
      return data.data;
    }
  );

  const availableUrls = useMemo(
    () => new Set(queryAdditionals.map((q) => q.url)),
    [queryAdditionals]
  );

  const hasAvailableGroups = useMemo(
    () =>
      Object.keys(groupDefinitions).some((mainUrl) =>
        availableUrls.has(mainUrl)
      ),
    [availableUrls]
  );

  const safeSetSelected = (updater) => {
    setSelectedQueries((prev) => {
      const next = updater(prev);
      // Dedup + filtra só o que a empresa possui
      const filtered = next.filter((u) => availableUrls.has(u));
      return Array.from(new Set(filtered));
    });
  };

  const handleCheckboxChange = (queryId, isChecked, dependencyId = null) => {
    // Bloqueia dependente sem o principal
    if (dependencyId && !selectedQueries.includes(dependencyId)) {
      setWarning("Você precisa escolher a opção principal antes.");
      setTimeout(() => setWarning(""), 3500);
      return;
    }

    safeSetSelected((prev) => {
      let updated = isChecked
        ? [...prev, queryId]
        : prev.filter((id) => id !== queryId);

      // Regra automática: Risco Completo Positivo PF adiciona Protesto Avançado PF
      if (queryId === RISCO_COMPLETO_PF && isChecked) {
        if (
          !updated.includes(PROTESTO_AVANCADO_PF) &&
          availableUrls.has(PROTESTO_AVANCADO_PF)
        ) {
          updated.push(PROTESTO_AVANCADO_PF);
        }
      }

      // Desmarcando o principal, remove o dependente automático
      if (queryId === RISCO_COMPLETO_PF && !isChecked) {
        updated = updated.filter((id) => id !== PROTESTO_AVANCADO_PF);
      }

      return updated;
    });
  };

  return (
    <>
      {warning && (
        <div className="fixed bottom-5 right-5 z-50">
          <Toast className="bg-yellow-100 text-yellow-800 shadow-lg rounded-lg">
            <HiExclamationCircle className="h-5 w-5 text-yellow-600" />
            <div className="text-sm font-medium ml-2">{warning}</div>
          </Toast>
        </div>
      )}

      {queryAdditionals.length > 0 && hasAvailableGroups && (
        <div>
          <div className="font-semibold mb-1 title-syncx">
            Incremente sua consulta{" "}
            <AiOutlineQuestionCircle
              className="cursor-pointer"
              onClick={() => setOpenModal(true)}
              title="Ajuda"
            />
          </div>
          <small className="mb-4 block mt-1 text-gray-600 italic">
            Selecione pelo menos uma das opções abaixo:
          </small>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4 max-w-xl">
            {Object.entries(groupDefinitions).map(
              ([mainUrl, { label, dependents }]) => {
                const isMainAvailable = availableUrls.has(mainUrl);
                if (!isMainAvailable) return null;

                const isMainSelected = selectedQueries.includes(mainUrl);

                return (
                  <div key={mainUrl} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={mainUrl}
                        checked={isMainSelected}
                        onChange={(e) =>
                          handleCheckboxChange(mainUrl, e.target.checked)
                        }
                      />
                      <Label htmlFor={mainUrl} className="font-bold">
                        {label}
                      </Label>
                    </div>

                    {/* Dependentes opcionais deste grupo */}
                    {dependents.map(({ url, label }) => {
                      if (!availableUrls.has(url)) return null;
                      const depChecked = selectedQueries.includes(url);

                      return (
                        <div key={url} className="flex items-center gap-2">
                          <Checkbox
                            id={url}
                            checked={depChecked}
                            onChange={(e) =>
                              handleCheckboxChange(
                                url,
                                e.target.checked,
                                mainUrl
                              )
                            }
                          />
                          <Label htmlFor={url} className="whitespace-nowrap">
                            {label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      <Modal show={openModal} onClose={() => setOpenModal(false)}>
        <ModalHeader>Adicionais PF</ModalHeader>
        <ModalBody>
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Marque apenas o que agrega valor à sua análise. Os pacotes{" "}
              <strong>Max</strong> e <strong>Risco</strong> permitem combinar
              insumos rapidamente.
            </p>
            <p>
              Ao ativar <strong>Risco Completo Positivo PF</strong>, o{" "}
              <em>Protesto Nacional Avançado PF</em> é incluído automaticamente
              para garantir cobertura mínima.
            </p>
            <p>
              Você pode adicionar dependentes (quando disponíveis) para
              enriquecer a leitura (ex.: Cadastral Plus, Score Positivo, etc.).
            </p>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default AdditionalQueryAdvancedPF;
